import { KeyboardEvent, ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@Utils';

import { ChainAvatar } from '@Components/ConnectedChainCard';
import { Store } from '@Models';
import { getRetailerColor, getRetailerName, getRetailerShort } from '../../../FieldServices/constants';
import { SECTION_LABEL_CLASS_NAME } from '../constants';
import Checkbox from './checkbox';

type FilterScope =
  | { kind: 'all' }
  | { kind: 'retailer'; retailer: string }
  | { kind: 'region'; retailer: string; region: string };

const scopeKey = (scope: FilterScope): string => {
  switch (scope.kind) {
    case 'all':
      return 'all';
    case 'retailer':
      return `retailer:${scope.retailer}`;
    default:
      return `region:${scope.retailer}:${scope.region}`;
  }
};

interface SelectionState {
  checked: boolean;
  indeterminate: boolean;
}

interface RetailerTreeProps {
  stores: Store[];
  storesByRetailer: Map<string, Store[]>;
  regionsByRetailer: Map<string, string[]>;
  retailers: string[];
  scope: FilterScope;
  expandedRetailers: Set<string>;
  lockedRetailer: string | null;
  oneRetailerHint: string;
  retailersLabel: string;
  allRetailersLabel: string;
  getSelectionState: (targetStores: Store[]) => SelectionState;
  onAllRetailersClick: () => void;
  onRetailerRowClick: (retailer: string) => void;
  onToggleRetailerExpanded: (retailer: string) => void;
  onRegionRowClick: (retailer: string, region: string) => void;
  onToggleStoresSelected: (targetStores: Store[], selected: boolean) => void;
}

interface TreeRowProps {
  label: string;
  count: number;
  depth: 0 | 1 | 2;
  isActive: boolean;
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  disabledHint: string;
  onRowClick: () => void;
  onToggleSelected: () => void;
  icon?: ReactNode;
  chevron?: { expanded: boolean; onToggle: () => void };
}

const TreeRow = ({
  label,
  count,
  depth,
  isActive,
  checked,
  indeterminate,
  disabled,
  disabledHint,
  onRowClick,
  onToggleSelected,
  icon,
  chevron,
}: TreeRowProps) => (
  <div
    role="button"
    tabIndex={0}
    title={disabled ? disabledHint : undefined}
    onClick={onRowClick}
    onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onRowClick();
      }
    }}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-md py-1.5 pr-2 text-[13px] transition-colors',
      isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-700 hover:bg-white',
    )}
    style={{ paddingLeft: 8 + depth * 16 }}>
    {chevron ? (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          chevron.onToggle();
        }}
        className="flex h-4 w-4 shrink-0 items-center justify-center text-gray-400">
        {chevron.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
    ) : (
      depth > 0 && <span className="h-4 w-4 shrink-0" />
    )}
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={onToggleSelected}
      onClick={(event) => event.stopPropagation()}
    />
    {icon}
    <span className={cn('flex-1 truncate font-medium', disabled && 'text-gray-400')}>{label}</span>
    <span className="shrink-0 text-xs text-gray-400">{count}</span>
  </div>
);

const RetailerTree = ({
  stores,
  storesByRetailer,
  regionsByRetailer,
  retailers,
  scope,
  expandedRetailers,
  lockedRetailer,
  oneRetailerHint,
  retailersLabel,
  allRetailersLabel,
  getSelectionState,
  onAllRetailersClick,
  onRetailerRowClick,
  onToggleRetailerExpanded,
  onRegionRowClick,
  onToggleStoresSelected,
}: RetailerTreeProps) => {
  const allSelectionState = getSelectionState(stores);

  return (
    <div className="flex w-72 shrink-0 flex-col gap-3 border-r border-gray-200 bg-gray-50 p-4">
      <span className={SECTION_LABEL_CLASS_NAME}>{retailersLabel}</span>
      <div className="flex max-h-[420px] flex-col gap-0.5 overflow-y-auto pr-1">
        <TreeRow
          label={allRetailersLabel}
          count={stores.length}
          depth={0}
          isActive={scopeKey(scope) === 'all'}
          checked={allSelectionState.checked}
          indeterminate={allSelectionState.indeterminate}
          disabled={retailers.length > 1}
          disabledHint={oneRetailerHint}
          onRowClick={onAllRetailersClick}
          onToggleSelected={() => onToggleStoresSelected(stores, !allSelectionState.checked)}
        />
        {retailers.map((retailer) => {
          const retailerStores = storesByRetailer.get(retailer) ?? [];
          const retailerScope: FilterScope = { kind: 'retailer', retailer };
          const retailerState = getSelectionState(retailerStores);
          const isLockedOut = lockedRetailer !== null && lockedRetailer !== retailer;
          const isExpanded = expandedRetailers.has(retailer);
          const regions = regionsByRetailer.get(retailer) ?? [];
          return (
            <div key={retailer} className="flex flex-col gap-0.5">
              <TreeRow
                label={getRetailerName(retailer)}
                count={retailerStores.length}
                depth={1}
                isActive={scopeKey(scope) === scopeKey(retailerScope)}
                checked={retailerState.checked}
                indeterminate={retailerState.indeterminate}
                disabled={isLockedOut}
                disabledHint={oneRetailerHint}
                icon={
                  <ChainAvatar
                    size="sm"
                    className="h-[22px] w-[22px] rounded-[5px] text-[8px]"
                    initials={getRetailerShort(retailer)}
                    color={getRetailerColor(retailer)}
                    textColor="#FFFFFF"
                    alt={getRetailerName(retailer)}
                  />
                }
                chevron={{ expanded: isExpanded, onToggle: () => onToggleRetailerExpanded(retailer) }}
                onRowClick={() => onRetailerRowClick(retailer)}
                onToggleSelected={() => onToggleStoresSelected(retailerStores, !retailerState.checked)}
              />
              {isExpanded &&
                regions.map((region) => {
                  const regionStores = retailerStores.filter((store) => store.region === region);
                  const regionScope: FilterScope = { kind: 'region', retailer, region };
                  const regionState = getSelectionState(regionStores);
                  return (
                    <TreeRow
                      key={region}
                      label={region}
                      count={regionStores.length}
                      depth={2}
                      isActive={scopeKey(scope) === scopeKey(regionScope)}
                      checked={regionState.checked}
                      indeterminate={regionState.indeterminate}
                      disabled={isLockedOut}
                      disabledHint={oneRetailerHint}
                      onRowClick={() => onRegionRowClick(retailer, region)}
                      onToggleSelected={() => onToggleStoresSelected(regionStores, !regionState.checked)}
                    />
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RetailerTree;
export type { FilterScope, SelectionState };
